import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bookmark, ChevronDown, Check, Plus, Pencil, Save, Star, StarOff, Trash2, MoreHorizontal } from "lucide-react";

// Private, per-user saved views for a screen's filter/sort/toggle state.
// Props:
//   screen: unique key for the screen (e.g. "tasks")
//   state: the current live filter state object to save/update
//   onApply(state): apply a chosen view's state to the screen
//   defaultState: the screen's built-in starting state (for "Default view")
export function SavedViews({ screen, state, onApply, defaultState, className = "" }) {
  const [views, setViews] = useState([]);
  const [activeId, setActiveId] = useState(null); // null = built-in Default view
  const [nameOpen, setNameOpen] = useState(false);
  const [nameVal, setNameVal] = useState("");
  const [editId, setEditId] = useState(null); // set when renaming
  const [busy, setBusy] = useState(false);
  const applied = useRef(false);
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const defaultRef = useRef(defaultState);
  defaultRef.current = defaultState;

  const load = async (autoApply = false) => {
    try {
      const { data } = await api.get(`/saved-views?screen=${encodeURIComponent(screen)}`);
      const list = data.views || [];
      setViews(list);
      if (autoApply && !applied.current) {
        applied.current = true;
        const def = list.find((v) => v.is_default);
        if (def) { setActiveId(def.id); onApplyRef.current && onApplyRef.current(def.state || {}); }
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, [screen]);

  const activeName = activeId ? (views.find((v) => v.id === activeId)?.name || "Default view") : "Default view";

  const pickDefault = () => { setActiveId(null); onApply && onApply(defaultRef.current || {}); };
  const pickView = (v) => { setActiveId(v.id); onApply && onApply(v.state || {}); };

  const openSave = () => { setEditId(null); setNameVal(""); setNameOpen(true); };
  const openRename = (v) => { setEditId(v.id); setNameVal(v.name); setNameOpen(true); };

  const submitName = async () => {
    const nm = nameVal.trim();
    if (!nm) return;
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/saved-views/${editId}`, { name: nm });
        toast.success("View renamed");
      } else {
        const { data } = await api.post(`/saved-views`, { screen, name: nm, state });
        setActiveId(data.id);
        toast.success(`Saved view "${nm}"`);
      }
      setNameOpen(false); setNameVal(""); setEditId(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const updateView = async (v) => {
    try {
      await api.put(`/saved-views/${v.id}`, { state });
      setActiveId(v.id);
      toast.success(`"${v.name}" updated with current filters`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleDefault = async (v) => {
    try {
      await api.put(`/saved-views/${v.id}`, { is_default: !v.is_default });
      toast.success(v.is_default ? "Removed as default" : `"${v.name}" is now your default`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeView = async (v) => {
    if (!(await confirmDelete({ message: `Delete the saved view "${v.name}"? This can't be undone.` }))) return;
    try {
      await api.delete(`/saved-views/${v.id}`);
      if (activeId === v.id) setActiveId(null);
      toast.success("View deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="saved-views-trigger" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border bg-white text-[#06060C] border-[#EEEFEF] hover:bg-[#F6F6FD] transition-colors ${className}`}>
            <Bookmark className="h-3.5 w-3.5 text-[#5b2fd6]" />
            <span className="max-w-[140px] truncate">{activeName}</span>
            <ChevronDown className="h-3.5 w-3.5 text-[#9FA1A4]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60 bg-white border-[#EEEFEF]">
          <DropdownMenuLabel className="text-[10px] font-mono-data uppercase tracking-wider text-[#9FA1A4]">Your views</DropdownMenuLabel>
          <DropdownMenuItem data-testid="saved-view-default" onClick={pickDefault} className="cursor-pointer focus:bg-[#F6F6FD]">
            <span className="flex-1">Default view</span>
            {activeId === null && <Check className="h-4 w-4 text-[#5b2fd6]" />}
          </DropdownMenuItem>
          {views.length > 0 && <DropdownMenuSeparator className="bg-[#f0f0f3]" />}
          {views.map((v) => (
            <DropdownMenuSub key={v.id}>
              <div className="flex items-center">
                <DropdownMenuItem data-testid={`saved-view-item-${v.id}`} onClick={() => pickView(v)} className="cursor-pointer focus:bg-[#F6F6FD] flex-1 min-w-0">
                  {v.is_default && <Star className="h-3.5 w-3.5 text-[#d6a400] shrink-0" fill="#f2c400" />}
                  <span className="flex-1 truncate">{v.name}</span>
                  {activeId === v.id && <Check className="h-4 w-4 text-[#5b2fd6] shrink-0" />}
                </DropdownMenuItem>
                <DropdownMenuSubTrigger data-testid={`saved-view-menu-${v.id}`} className="px-1.5 py-1.5 rounded-md hover:bg-[#F6F6FD] [&>svg:last-child]:hidden">
                  <MoreHorizontal className="h-4 w-4 text-[#9FA1A4]" />
                </DropdownMenuSubTrigger>
              </div>
              <DropdownMenuSubContent className="w-44 bg-white border-[#EEEFEF]">
                <DropdownMenuItem data-testid={`saved-view-rename-${v.id}`} onClick={() => openRename(v)} className="cursor-pointer focus:bg-[#F6F6FD]"><Pencil className="h-3.5 w-3.5 mr-2" /> Rename</DropdownMenuItem>
                <DropdownMenuItem data-testid={`saved-view-update-${v.id}`} onClick={() => updateView(v)} className="cursor-pointer focus:bg-[#F6F6FD]"><Save className="h-3.5 w-3.5 mr-2" /> Update to current</DropdownMenuItem>
                <DropdownMenuItem data-testid={`saved-view-default-${v.id}`} onClick={() => toggleDefault(v)} className="cursor-pointer focus:bg-[#F6F6FD]">{v.is_default ? <><StarOff className="h-3.5 w-3.5 mr-2" /> Remove default</> : <><Star className="h-3.5 w-3.5 mr-2" /> Set as default</>}</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#f0f0f3]" />
                <DropdownMenuItem data-testid={`saved-view-delete-${v.id}`} onClick={() => removeView(v)} className="cursor-pointer text-rose-600 focus:bg-rose-50 focus:text-rose-600"><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))}
          <DropdownMenuSeparator className="bg-[#f0f0f3]" />
          <DropdownMenuItem data-testid="saved-view-save-current" onClick={openSave} className="cursor-pointer focus:bg-[#F6F6FD] text-[#5b2fd6]"><Plus className="h-4 w-4 mr-2" /> Save current view</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={nameOpen} onOpenChange={(o) => { if (!o) { setNameOpen(false); setNameVal(""); setEditId(null); } }}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-sm" data-testid="saved-view-name-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Rename view" : "Save current view"}</DialogTitle>
            <DialogDescription className="text-[#9FA1A4]">{editId ? "Give this saved view a new name." : "Capture the current filters, sort and toggles as a private view you can jump back to."}</DialogDescription>
          </DialogHeader>
          <Input data-testid="saved-view-name-input" autoFocus value={nameVal} onChange={(e) => setNameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitName(); } }} placeholder="e.g. My overdue work" className="bg-[#F6F6FD] border-[#EEEFEF]" />
          <DialogFooter>
            <Button data-testid="saved-view-name-cancel" variant="outline" onClick={() => { setNameOpen(false); setNameVal(""); setEditId(null); }} className="border-[#EEEFEF] rounded-full">Cancel</Button>
            <Button data-testid="saved-view-name-save" onClick={submitName} disabled={busy || !nameVal.trim()} className="bg-[#06060C] text-white rounded-full">{editId ? "Rename" : "Save view"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
